import { useState } from 'react'

import { Box, Tab, Tabs, Tooltip } from '@mui/material'
import { format } from 'date-fns'
import { Link, useParams } from 'react-router-dom'

import AccountPayableDocumentsHistoryTab from 'components/accountsPayable/AccountPayableDocuments/AccountPayableDocumentsHistoryTab'
import AccountPayableDocumentsJobDetailsTab from 'components/accountsPayable/AccountPayableDocuments/AccountPayableDocumentsJobDetailsTab'
import AccountPayableDocumentsRemittanceAdviceTab from 'components/accountsPayable/AccountPayableDocuments/AccountPayableDocumentsRemittanceAdviceTab'
import { TabPanel } from 'components/common'
import { ISO_8601_DATE_FORMAT } from 'constants/DateFormats'
import {
  AccountPayableRecordStatus,
  AccountsPayableRecordCategory,
  useGetAccountPayableRecordQuery,
} from 'generated/graphql'
import { useHashToSelectTab } from 'helpers'
import { Restricted, usePermission } from 'providers'

enum TabId {
  JobDetails = 0,
  History = 1,
  RemittanceAdvice = 2,
}

const tabsList = [
  { id: TabId.JobDetails, label: 'Job Details', hash: '#job-details' },
  { id: TabId.History, label: 'History', hash: '#history' },
  { id: TabId.RemittanceAdvice, label: 'Remittance Advice', hash: '#remittance-advice' },
]

function TabsPanel() {
  const [tabValue, setTabValue] = useState<number | false>(false)

  const [isAllowedToViewHistory] = usePermission('ViewAccountPayableRecordLogs')
  const { id } = useParams<{ id?: string }>()
  const { data: apr } = useGetAccountPayableRecordQuery({
    fetchPolicy: 'network-only',
    variables: { id: Number(id) },
    skip: !id,
  })

  const permissionMap = {
    [TabId.History]: isAllowedToViewHistory,
  }

  const getTabPermission = (tabId: TabId) =>
    permissionMap[tabId] === undefined || permissionMap[tabId] ? 'block' : 'none'

  useHashToSelectTab(tabsList, setTabValue)

  const getTabDisabledStatus = (tabId: TabId) => {
    if (tabId === TabId.JobDetails || tabId === TabId.History) return false

    if (apr?.accountPayableRecord?.category !== AccountsPayableRecordCategory.SmartPay) 
      return !apr?.accountPayableRecord?.remittanceAdviceSent

    const todayFormatted = format(new Date(), ISO_8601_DATE_FORMAT)
    const dueDateFormatted = format(new Date(apr?.accountPayableRecord.bookingInvoice?.dueDate || ''), ISO_8601_DATE_FORMAT)
    const isArchived = apr?.accountPayableRecord?.status === AccountPayableRecordStatus.Archived

    return (todayFormatted < dueDateFormatted || !isArchived)
  }

  const getTooltipTitle = (tabId: TabId) => {
    if (tabId === TabId.RemittanceAdvice) {
      if (apr?.accountPayableRecord?.payedExternally) {
        return 'Remittance Advice is not available for records that have been paid externally.'
      }
      
      if (getTabDisabledStatus(tabId)) {
        return 'Remittance Advice will be available once it has been sent.'
      }
    }

    return ''
  }

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pt: 2 }}>
        <Tabs value={tabValue} aria-label='account-payable-documents'>
          {tabsList.map((tab) =>
            getTabDisabledStatus(tab.id) ? (
              <Tooltip key={tab.id} title={getTooltipTitle(tab.id)} arrow>
                <span>
                  <Tab
                    label={tab.label}
                    disabled
                    component={Link}
                    sx={{ display: getTabPermission(tab.id) }}
                    to={{ hash: tab.hash }}
                  />
                </span>
              </Tooltip>
            ) : (
              <Tab
                key={tab.id}
                label={tab.label}
                component={Link}
                sx={{ display: getTabPermission(tab.id) }}
                to={{ hash: tab.hash }}
              />
            ),
          )}
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={TabId.JobDetails} elevation={0}>
        <AccountPayableDocumentsJobDetailsTab />
      </TabPanel>
      <Restricted to='ViewAccountPayableRecordLogs'>
        <TabPanel value={tabValue} index={TabId.History} elevation={0}>
          <AccountPayableDocumentsHistoryTab />
        </TabPanel>
      </Restricted>
      <TabPanel value={tabValue} index={TabId.RemittanceAdvice} elevation={0}>
        <AccountPayableDocumentsRemittanceAdviceTab />
      </TabPanel>
    </>
  )
}

export default TabsPanel
